import { IAuthService } from '@base/features/auth/model/interfaces';
import { MessagesService } from '@base/features/chat/messagesService';
import BaseTable, { MAX_PLAYERS } from '@base/features/game/model/baseTable';
import {
  IGameProvider,
  IGameService,
  IInviteService,
  ITable,
  ITableRepository,
  TableOptions,
} from '@base/features/game/model/interfaces';
import SoundProvider from '@base/features/game/model/soundProvider';
import BufferReader from '@base/features/socket/bufferReader';
import Codes from '@base/features/socket/codes';
import Utils from '@base/features/socket/utils';
import { ILanguageService } from '@base/features/i18n/model/interfaces';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import { IPlayer, IPlayersOnlineRepository } from '@base/features/player/model/interfaces';
import { BasePreferences, IPreferencesService } from '@base/features/preferences/model/types';
import { ISocketClient } from '@base/features/socket/interfaces';
import { ITicketService } from '@base/features/tickets/model/interfaces';
import { Subject } from 'rxjs';

export default class LobbyService<P extends BasePreferences = BasePreferences, T extends ITable = BaseTable> {
  onTablesListChanged = new Subject<void>();
  onTableOwnerChanged = new Subject<IPlayer>();
  onMeJoinedTable = new Subject<number>();
  onMyTableJoined = new Subject<boolean>();

  constructor(
    private socket: ISocketClient,
    private players: IPlayersOnlineRepository<P, T>,
    private tables: ITableRepository<T>,
    private message: MessagesService<P, T>,
    private invites: IInviteService,
    private gameProvider: IGameProvider<P, T>,
    private preferencesService: IPreferencesService<P>,
    private language: ILanguageService,
    private sound: SoundProvider<P>,
    private modal: IModalProvider,
    private ticketsService: ITicketService,
    private gameService: IGameService,
    private authService: IAuthService
  ) {}

  logout() {
    this.players.clear();
    this.tables.cleanTables();
    this.message.cleanMessagesLobby();
    this.ticketsService.accountBalance.next(0);
    this.authService.logout();
  }

  private onReceivePlayerSwap(data: BufferReader) {
    const swap: { tid: number; size: number; players: number[] } = {
      tid: data.readInt16(),
      size: data.readInt8(),
      players: [],
    };

    for (let i = 0; i < swap.size; i++) {
      swap.players.push(data.readInt32());
    }

    const table = this.tables.getTable(swap.tid);
    const player1 = this.players.getPlayerOnline(swap.players[0]);
    const player2 = this.players.getPlayerOnline(swap.players[1]);

    if (!(player1 && player2 && table)) {
      throw new Error('Error: onReceivePlayerSwap');
    }

    console.log("Swapping players");

    table.swapPlayers(player1, player2);
  }

  private onReceiveError(data: BufferReader) {
    const category = data.readUint8();
    const number = data.readUint8();
    const code = category + '-' + number;
    console.warn('Server error: ' + code + ' ' + Codes.ErrorTypes.names[code]);

    switch (code) {
      case Codes.ErrorTypes.LOGIN_READY:
        this.players.onPlayersListChanged.next();
        this.players.onLoginReady.next(true);
        break;
      case Codes.ErrorTypes.TABLE_BANNED:
        this.modal.show({
          body: this.language.translate('you_have_been_blocked_from_table'),
          buttons: [{ title: this.language.translate('ok'), onClick: () => this.modal.hide() }],
        });
        break;
      case Codes.ErrorTypes.TABLE_FULL:
        this.modal.show({
          body: this.language.translate('table_full'),
          buttons: [{ title: this.language.translate('ok'), onClick: () => this.modal.hide() }],
        });
        break;
      //ToDO временная модалка, выяснить у сервера причины этой ошибки
      case Codes.ErrorTypes.TABLE_UNINVITED:
        this.modal.show({
          body: this.language.translate('must_be_invited_to_join_table'),
          buttons: [{ title: this.language.translate('ok'), onClick: () => this.modal.hide() }],
        });
        break;
      case Codes.ErrorTypes.TOUR_NO_ACCOUNT:
        this.modal.show({
          body: this.language.translate('insufficient_number_of_tickets'),
          buttons: [{ title: this.language.translate('ok'), onClick: () => this.modal.hide() }],
        });
        break;
      case Codes.ErrorTypes.LOGIN_NONEXIST:
      case Codes.ErrorTypes.LOGIN_RECONNECT:
      case Codes.ErrorTypes.LOGIN_RC_FORCE:
        this.modal.show({
          title: this.language.translate('error'),
          body: this.language.translate('session_expired_relogin'),
          buttons: [
            {
              title: this.language.translate('reconnect'),
              onClick: () => {
                this.logout();
                this.modal.hide();
              },
            },
          ],
        });
        break;
      case Codes.ErrorTypes.PLAY_ILLEGAL:
      case Codes.ErrorTypes.PLAY_NO_START:
      case Codes.ErrorTypes.PLAY_NO_TURN:
      case Codes.ErrorTypes.PLAY_REFUSED:
        throw new Error('Server rules violated! Error code: ' + code);
    }
  }

  private checkParams(params: any) {
    let error: any;
    error = this.tables.checkParams(params);
    error = this.players.checkParams(params);
    return error;
  }

  private onReceiveTablePlayer(data: BufferReader) {
    const tid = data.readInt16();
    const seat = data.readUint8();
    const pid = data.readInt16();

    const error = this.checkParams({ tid, seat, pid });
    if (error) {
      console.error('Error: tablePlayerJoined', error);
      return;
    }
    const table = this.tables.getTable(tid);
    const player = this.players.getPlayerOnline(pid);

    let wasWatcher = false;
    let wasReconnect = false;
    for (let i = 0; i < MAX_PLAYERS; ++i) {
      const p = table?.getPlayerBySeat(i);
      if (p && p === player) {
        if (table && i < table.getMaxPlayersCount()) {
          // Был игрок
          wasReconnect = true;
        } else {
          // Был наблюдатель
          table?.setPlayer(undefined, i);
          this.tables.updatePlayerOnTable(tid, i);
          wasWatcher = true;
        }
        break;
      }
    }

    if (!player?.isMyTable(table)) {
      player?.setTable(table);
    }

    table?.setPlayer(player, seat);
    this.tables.updatePlayerOnTable(tid, seat, player);

    if (table && seat >= 0 && seat < table.getMaxPlayersCount()) {
      // join to play
      if (table?.isAllPlaying()) {
        this.invites.removeInvites(table);
        if (this.players.me?.isMyTable(table) && this.players.me.isPlayingTable(table)) {
          let opponent;
          if (table.getPlayerBySeat(0)?.id === this.players.me.id) {
            opponent = table.getPlayerBySeat(1);
          } else {
            opponent = table.getPlayerBySeat(0);
          }
          table.showProjectedRating(this.players.me, opponent);
        }
      }
    }

    // it's me joining
    if (player === this.players.me) {
      this.message?.createChatMessage({
        text: Utils.formatString(
          '%s <%s>',
          this.language.translate('you_have_joined_table'),
          this.players.me?.getTable()?.getNumber()
        ),
        type: Codes.MessageTypes.ALL,
      });

      if (wasWatcher) {
        console.log(wasWatcher);
      } else if (wasReconnect) {
        console.log(wasReconnect);
      } else {
        if (table && this.players.me) {
          const gameState = this.gameProvider.createGameState(
            //@ts-ignore
            table,
            this.tables,
            this.players,
            this,
            this.sound,
            this.preferencesService,
            this.message,
            this.modal,
            this.gameService,
            this.socket
          );
          table.setGameState(gameState);
          this.players.me.getTable()?.getGameState().onPlayerJoined(this.players.me);
          this.onMeJoinedTable.next(table.tid);
        }
      }
    }

    if (
      table &&
      this.players.me?.isMyTable(table) &&
      this.players.me?.isPlayingTable(table) &&
      player?.id !== this.players.me.id
    ) {
      this.onMyTableJoined.next(true);
      this.sound.play(this.sound.codes.BELL, this.sound.categories.JOIN_TABLE);
    }
  }

  private onReceiveTableLeave(data: BufferReader) {
    const tid = data.readInt16();
    let seat = data.readUint8();
    const pid = data.readInt16();
    const error = this.checkParams({ tid, seat, pid });
    if (error) {
      return console.error('Error: tablePlayerLeft', error);
    }

    const table = this.tables.getTable(tid);
    const player = this.players.getPlayerOnline(pid);

    let him = null; // игрок
    if (seat >= 0 && seat < MAX_PLAYERS) {
      him = table?.getPlayerBySeat(seat);
    } else {
      him = player;
    }

    if (!him) {
      return console.error('Error: onTablePlayerLeft: cannot find the player who left the table', tid, pid);
    }

    seat = him.getSeat(table);

    // Если ушли со своего стола
    if (him === this.players.me && this.players.me.isMyTable(table)) {
      this.message?.createChatMessage({
        text: Utils.formatString(
          '%s <%s>',
          this.language.translate('you_have_left_table'),
          this.players.me.getTable()?.getNumber()
        ),
        type: Codes.MessageTypes.ALL,
      });

      // Удаление игрока со стола
      const s = this.players.me.getSeat(table);
      this.players.me.setTable(undefined);
      this.onMeJoinedTable.next(-1);
      this.onMyTableJoined.next(undefined);
      if (s >= 0 && s < MAX_PLAYERS) {
        table?.setPlayer(undefined, s);
        this.tables.updatePlayerOnTable(tid, s);
      }
      // Если на столе не осталось игроков - удаление стола
      if (table && !table.hasPlayers()) {
        this.invites.removeInvites(table);
        table.clearChatMessages();
        this.tables.delete(table.tid);
        this.onTablesListChanged.next();
      }

      table?.resetGameState();
      this.players.onPlayersListChanged.next();
      return;
    }
    // Если другой игрок ушёл с нашего стола
    else if (him !== this.players.me && this.players.me?.isMyTable(table)) {
      if (!him.isBot()) {
        const whoStr = this.language.translate(
          table && seat >= 0 && seat < table.getMaxPlayersCount() ? 'player' : 'watcher'
        );
        this.message?.createChatMessage({
          type: Codes.MessageTypes.TABLE,
          text: him.getName() + ' (' + whoStr + ') ' + this.language.translate('left_the_table'),
          tid: table?.tid,
        });
      } else if (him.isPhantom() /* && !forced */) {
        return;
      }
    }
    this.onMyTableJoined.next(undefined);
    him.setTable(undefined);
    if (seat >= 0 && seat < MAX_PLAYERS) {
      table?.setPlayer(undefined, seat);
      this.tables.updatePlayerOnTable(tid, seat);
    }

    // На столе не осталось игроков
    if (!table?.hasPlayers()) {
      // Я watcher за этим столом
      if (table?.seats.find((p: IPlayer | undefined) => p?.userId === this.players.me?.userId)) {
        this.leaveTable(table);
        this.onMeJoinedTable.next(-1);
      }
      // На столе не осталось никого
      if (table && !table.hasPeople()) {
        this.invites.removeInvites(table);
        table?.clearChatMessages();
        this.tables.delete(table.tid);
        this.onTablesListChanged.next();
      }
    }
  }

  playTable(tableId: number, mode?: number, eulaAccepted?: boolean): boolean {
    if (!this.players.me) return false;

    //Не можем играть 2 стола одновременно
    if (this.players.me.getTable()?.getGameState().imPlaying()) {
      this.modal.show({
        title: this.language.translate('warning'),
        body: this.language.translate('too_many_tables_for_one_player'),
        buttons: [
          {
            title: this.language.translate('join_table'),
            onClick: () => {
              this.leaveTable(this.players.me?.getTable());
              this.modal.hide();
              this.playTable(tableId, mode, eulaAccepted);
            },
          },
          {
            title: this.language.translate('cancel'),
            onClick: () => this.modal.hide(),
          },
        ],
      });
      return false;
    }

    // Если мы уже находимся наблюдателем на этом столе
    if (this.players.me.getTable()?.getGameState().imWatching()) {
      this.leaveTable();
      this.playTable(tableId, mode, eulaAccepted);
    }

    const table = this.tables.getTable(tableId);
    const tableNumber = String(table?.getNumber());
    const title = this.language.translate(
      'join_table_error_1s_2s',
      this.language.translate('join_l'),
      tableNumber
    );
    if (!table) {
      //если нет стола
      this.modal.show({
        title,
        body: this.language.translate('table_not_found'),
        buttons: [{ title: this.language.translate('ok'), onClick: () => this.modal.hide() }],
      });
      return false;
    } else if (table.isAllPlaying()) {
      // Если все игроки уже присутствуют
      this.modal.show({
        title,
        body: this.language.translate('table_full'),
        buttons: [{ title: this.language.translate('ok'), onClick: () => this.modal.hide() }],
      });
      return false;
    } else if (table.getCost() > 0) {
      //если стол тикетный
      if (!this.ticketsService.haveSufficientBalance(table)) {
        return false;
      }
      if (table.cost / 2 > this.players.me.getMoneyLimit()) {
        this.modal.show({
          title,
          body: Utils.formatString(
            this.language.translate('can_play_arg_ticket_games_max'),
            this.players.me.getMoneyLimit()
          ),
          buttons: [
            {
              title: this.language.translate('cancel'),
              onClick: () => this.modal.hide(),
            },
            {
              title: this.language.translate('buy_tickets'),
              onClick: () => {
                this.modal.hide();
                this.ticketsService.openPaymentModal();
              },
            },
          ],
        });
        return false;
      }
    }

    if (table.cost > 0 && !eulaAccepted) {
      this.modal.showTermsAndConditions(tableId);
      return false;
    } else {
      const joinMode = mode || Codes.JoinMode.PLAY;
      this.joinTable(tableId, joinMode);
      return true;
    }
  }

  leaveTable(table?: ITable) {
    if (!this.players.me) return;
    if (!table) table = this.players.me.getTable();
    if (!table || !this.players.me.isMyTable(table)) return;
    this.joinTable(table.tid, Codes.JoinMode.LEAVE);
    this.onMeJoinedTable.next(-1);

    this.message?.createChatMessage({
      type: Codes.MessageTypes.ALL,
      text: Utils.formatString('%s <%s>', this.language.translate('you_have_left_table'), table.getNumber()),
    });

    const seat = this.players.me.getSeat(table);
    this.players.me.setTable(undefined);
    if (seat >= 0 && seat < MAX_PLAYERS) {
      table.setPlayer(undefined, seat);
    }

    table.resetGameState();

    if (!table.hasPlayers() || table.isPhantomTable()) {
      this.invites.removeInvites(table);
      table.clearChatMessages();
      this.tables.delete(table.tid);
      this.onTablesListChanged.next();
    }

    this.players.onPlayerLeft.next([table.tid, this.players.me.id]);
    this.players.onPlayersListChanged.next();
  }

  joinTable(tid: number, joinMode: number): void {
    const data = [Codes.Types.CP_TABLE_JOIN]
      .concat(Utils.decToInt16(tid))
      .concat(Utils.int8ToUint8(joinMode));
    this.socket.sendPacket(data);
  }

  watchTable(tableId: number): void {
    this.joinTable(tableId, Codes.JoinMode.WATCH);
  }

  sendTableOp(pid?: number, tid?: number, mode?: number): void {
    const data = [Codes.Types.CP_TABLE_OP]
      .concat(Utils.decToInt8(mode))
      .concat(Utils.decToInt16(tid))
      .concat(Utils.decToInt16(pid));
    this.socket.sendPacket(data);
  }

  private onReceiveTableOwn(data: BufferReader): void {
    const tid = data.readInt16();
    const ownerId = data.readUint16();
    const error = this.checkParams({ tid, pid: ownerId });
    if (error) return console.error('Error: onTableOwnerChanged', error, tid, ownerId);
    const newOwner = this.players.getPlayerOnline(ownerId);
    if (newOwner) this.tables.getTable(tid)?.setOwner(newOwner);
    this.onTableOwnerChanged.next(newOwner);
  }

  private onReceiveTableOpt(data: BufferReader) {
    const newTable: TableOptions = {
      tid: data.readInt16(),
      inviteOnly: data.readBoolean(),
      timeGame: data.readInt16(),
      timeGameAdd: data.readInt16(),
      ratingGame: data.readBoolean(),
      cost: data.readInt16(),
      reserved: data.readInt16(),
      gameColor: data.readInt16(),
      withTime: data.readBoolean(),
      timePerMove: data.readInt16(),
      withTimePerMove: data.readBoolean(),
      maxPoints: data.readInt16(),
      minPoints: data.readInt16(),
      ladder: data.readBoolean(),
      noWatchers: data.readBoolean(),
      signs: data.readInt32(),
    };

    const table = this.tables.updateDataTable(newTable);

    if (
      this.players.me &&
      this.players.me.isMyTable(table) &&
      table.isAllPlaying() &&
      this.players.me.isPlayingTable(table)
    ) {
      this.modal.show({
        body: this.language.translate('opponents_use_the_same_computer'),
        buttons: [{ title: this.language.translate('ok'), onClick: () => this.modal.hide() }],
      });
    }
  }
}
